import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthContext } from '@/context/AuthContext';

interface InactivityDialogContextType {
    showInactivityDialog: boolean;
    setShowInactivityDialog: (show: boolean) => void;
    resetInactivityTimer: () => void;
}

const InactivityDialogContext = createContext<InactivityDialogContextType | undefined>(undefined);

export function useInactivityDialog() {
    const context = useContext(InactivityDialogContext);
    if (!context) {
        throw new Error('useInactivityDialog must be used within an InactivityDialogProvider');
    }
    return context;
}

interface InactivityDialogProviderProps {
    children: React.ReactNode;
    inactivityLimit?: number;
    logoutLimit?: number;
}

export function InactivityDialogProvider({
    children,
    inactivityLimit = 13 * 60 * 1000,
    logoutLimit = 2 * 60 * 1000
}: InactivityDialogProviderProps) {
    const { refreshAccessToken } = useAuth();
    const [showInactivityDialog, setShowInactivityDialog] = useState(false);
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
    const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isLogoutCountdownActive = useRef(false);
    const { logout } = useAuthContext();
    const navigate = useNavigate();

    const resetInactivityTimer = useCallback(() => {
        if (showInactivityDialog) return;

        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }

        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        isLogoutCountdownActive.current = false;

        inactivityTimerRef.current = setTimeout(() => {
            setShowInactivityDialog(true);
            isLogoutCountdownActive.current = true;

            logoutTimerRef.current = setTimeout(() => {
                handleLogout();
            }, logoutLimit);
        }, inactivityLimit);
    }, [inactivityLimit, logoutLimit, showInactivityDialog]);

    const handleStayLoggedIn = useCallback(() => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        isLogoutCountdownActive.current = false;
        refreshAccessToken();
        setShowInactivityDialog(false);
    }, []);

    const handleLogout = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }

        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        isLogoutCountdownActive.current = false;
        logout();
        setShowInactivityDialog(false);
    }, [navigate]);

    const startInactivityWatcher = useCallback(() => {
        const events = ["mousedown", "keydown", "touchstart", "mousemove"];

        const reset = () => {
            if (!isLogoutCountdownActive.current) {
                resetInactivityTimer();
            }
        };

        events.forEach((event) => {
            window.addEventListener(event, reset);
        });

        resetInactivityTimer();

        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, reset);
            });

            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }

            if (!isLogoutCountdownActive.current && logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }
        };
    }, [resetInactivityTimer]);

    useEffect(() => {
        const stopWatcher = startInactivityWatcher();
        return stopWatcher;
    }, [startInactivityWatcher]);

    useEffect(() => {
        if (!showInactivityDialog && !isLogoutCountdownActive.current) {
            resetInactivityTimer();
        }
    }, [showInactivityDialog, resetInactivityTimer]);

    return (
        <InactivityDialogContext.Provider
            value={{
                showInactivityDialog,
                setShowInactivityDialog,
                resetInactivityTimer
            }}
        >
            {children}
            <AlertDialog open={showInactivityDialog} onOpenChange={(open) => {
                if (!open) return;
                setShowInactivityDialog(open);
            }}>
                <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you still there?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You've been inactive for a while. For security reasons, you will be
                            logged out in 2 minutes unless you choose to stay logged in.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleLogout}>
                            Logout
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleStayLoggedIn}>
                            Stay Logged In
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </InactivityDialogContext.Provider>
    );
}
